import { getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth } from "./firebaseConfig";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { db } from "./firebaseConfig";
import { doc, setDoc } from "firebase/firestore";

// ✅ Initialize Google Auth Provider
const provider = new GoogleAuthProvider();

// ✅ Normal User Login (Prevents Admin Login)
export const doSignInWithEmailAndPassword = async (email, password) => {
  try {
    // Fetch user details from Firestore
    const userDocRef = doc(db, "users", email); // Lookup by email
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const existingData = userDocSnap.data();

      // ✅ Prevent admin users from logging in here
      if (existingData.admin === true) {
        throw new Error("Admins must log in through the Admin Login page.");
      }
    }

    // ✅ If the user is not an admin, proceed with Firebase authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // ✅ Ensure Firestore document exists for the user
    const userDocRefByUID = doc(db, "users", user.uid);
    const userDocSnapByUID = await getDoc(userDocRefByUID);

    if (!userDocSnapByUID.exists()) {
      await setDoc(userDocRefByUID, {
        email,
        admin: false,
        agreedTerms: true,
        agreedTermsTime: serverTimestamp(),
        wallet: {
          balance: 0,
          transactions: [],
          lastUpdated: serverTimestamp(),
        },
      });
    }

    return userCredential;
  } catch (error) {
    console.error("User Login Error:", error);
    throw new Error(error.message);
  }
};

// ✅ Admin Login (Only allows users with `admin: true`)
export const doAdminLogin = async (email, password) => {
  try {
    // Step 1: Perform Firebase Authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Step 2: Fetch user details from the `users` collection
    const userDocRef = doc(db, "users", user.uid); // Lookup by UID
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      await signOut(auth); // Logout if user data is not found
      throw new Error("User not found.");
    }

    const userData = userDocSnap.data();

    // Step 3: Check if the user is an admin
    if (!userData.admin) {
      await signOut(auth); // Logout non-admin users
      throw new Error("You are not authorized to access the admin dashboard.");
    }

  

    return userCredential;
  } catch (error) {
    console.error("Admin Login Error:", error);
    throw new Error(error.message || "Failed to log in as admin.");
  }
};

// ✅ Sign In with Google (Normal Users Only)
export const doSignInWithGoogle = async () => {
  try {
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    // Check if email is verified (though Google accounts should always be verified)
    if (!user.emailVerified) {
      throw new Error('Google account email is not verified');
    }

    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      // If user document doesn't exist, create it
      await setDoc(userDocRef, {
        email: user.email,
        admin: false,
        agreedTerms: true,
        agreedTermsTime: serverTimestamp(),
        wallet: {
          balance: 0,
          transactions: [],
          lastUpdated: serverTimestamp(),
        },
      });
    }

    return userCredential;
  } catch (error) {
    console.error("Google Sign-In Error:", error);
    
    // Handle specific Firebase errors
    if (error.code) {
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          throw new Error('Sign-in popup was closed before completing the process');
        case 'auth/cancelled-popup-request':
          throw new Error('Multiple popup requests were made too quickly');
        case 'auth/account-exists-with-different-credential':
          throw new Error('An account already exists with this email but different sign-in method');
        case 'auth/user-disabled':
          throw new Error('This account has been disabled');
        case 'auth/operation-not-allowed':
          throw new Error('Google sign-in is not enabled for this application');
        case 'auth/network-request-failed':
          throw new Error('Network error occurred during sign-in');
        default:
          throw new Error(`Authentication failed: ${error.message}`);
      }
    }
    
    // Handle non-Firebase errors
    throw new Error(error.message || 'An unknown error occurred during sign-in');
  }
};





// ✅ Fix: Properly export the function without overwriting
export const doSendPasswordResetEmail = async (email) => {
  return sendPasswordResetEmail(auth, email);
};


export const doCreateUserWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const { uid } = userCredential.user;

    // Create a new user document in Firestore with admin: false by default
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      email,
      admin: false, // Default to false, will not override existing admins
      agreedTerms: true,
      agreedTermsTime: serverTimestamp(),
      wallet: {
        balance: 0,
        transactions: [],
        lastUpdated: serverTimestamp(),
      },
    });

    return userCredential;
  } catch (error) {
    console.error("Sign-up Error:", error);
    throw new Error(error.message);
  }
};

export const SendEmailVerification = async (user) => {
  if (!user) throw new Error("User not found");

  try {
    await sendEmailVerification(user);
    console.log("✅ Verification email sent successfully!");
  } catch (error) {
    console.error("❌ Error sending verification email:", error);
    throw error; // Re-throw for better error handling
  }
};

// ✅ Sign Out
export const doSignOut = async () => {
  try {
    return await signOut(auth);
  } catch (error) {
    console.error("Sign-out Error:", error);
    throw new Error(error.message);
  }
};
